import { ZERO_IN_BIG_NUMBER } from '@shared/utils/constant'
import { toBigNumber } from '@injectivelabs/utils/big-number'
import { OrderSide } from '@injectivelabs/sdk-ts/types/light'
import type { BffDerivativeMarket } from '@shared/types'
import type { BigNumber } from '@injectivelabs/utils/big-number'

export const calculateLiquidationPrice = ({
  price,
  quantity,
  notionalWithLeverage,
  orderType,
  market: { maintenanceMarginRatio }
}: {
  price: string
  quantity: string
  orderType: OrderSide
  market: BffDerivativeMarket
  notionalWithLeverage: string
}): BigNumber => {
  if (!price || !quantity || !notionalWithLeverage) {
    return ZERO_IN_BIG_NUMBER
  }

  const isBuy = orderType === OrderSide.Buy

  const numerator = isBuy
    ? toBigNumber(notionalWithLeverage).minus(
        toBigNumber(price).times(quantity)
      )
    : toBigNumber(notionalWithLeverage).plus(toBigNumber(price).times(quantity))

  const maintenanceMarginRatioFactor = isBuy
    ? toBigNumber(maintenanceMarginRatio).minus(1)
    : toBigNumber(maintenanceMarginRatio)

  const denominator = isBuy
    ? maintenanceMarginRatioFactor.times(quantity)
    : maintenanceMarginRatioFactor.times(quantity).plus(quantity)

  const liquidationPrice = numerator.dividedBy(denominator)

  return liquidationPrice.gte(0) ? liquidationPrice : ZERO_IN_BIG_NUMBER
}

export const calculateScaledMarkPrice = ({
  market,
  markPriceNotScaled
}: {
  market: BffDerivativeMarket
  markPriceNotScaled: BigNumber
}) => {
  if (markPriceNotScaled.isZero()) {
    return markPriceNotScaled
  }

  if (!market.oracleScaleFactor) {
    return markPriceNotScaled
  }

  if (market.quoteToken.decimals === market.oracleScaleFactor) {
    return markPriceNotScaled
  }

  const oracleScalePriceDiff =
    market.oracleScaleFactor - market.quoteToken.decimals

  return markPriceNotScaled.times(toBigNumber(10).pow(oracleScalePriceDiff))
}
